import { ReadingsDataRawStateResponse } from "@inrange/building-manager-api-client";
import React, { useMemo, useState } from "react";
import { Col, Row, Table } from "react-bootstrap";
import Select from "react-select";
import { Formatters } from "./types";

const MAX_TABLE_ROWS = 5000;

const formatDuration = (startTime: string, endTime: string | null): string => {
  if (!endTime) return "Ongoing";

  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  const durationMs = end - start;

  if (durationMs < 0) return "Invalid";

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  }
  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${seconds}s`;
};

const StateDataTable = React.memo(
  ({
    data,
    selectedEsysUuid,
    startDate,
    endDate,
    formatters,
  }: {
    data: ReadingsDataRawStateResponse;
    selectedEsysUuid: string | undefined;
    startDate: string;
    endDate: string;
    formatters: Formatters;
  }) => {
    const [filterHardwareType, setFilterHardwareType] = useState<string[]>([]);
    const [filterSerialNumber, setFilterSerialNumber] = useState<string[]>([]);
    const [filterReadingName, setFilterReadingName] = useState<string[]>([]);
    const [filterState, setFilterState] = useState<string[]>([]);

    // Filter and process state transitions
    const filteredTransitions = useMemo(() => {
      let transitions = data.stateTransitions;

      // Filter by esysUuid
      if (selectedEsysUuid) {
        transitions = transitions.filter(
          (t) => t.esysUuid === selectedEsysUuid
        );
      }

      // Filter by date range
      if (startDate && endDate) {
        const { startMs, endMs } = formatters.dateRangeToTimestamps(
          startDate,
          endDate
        );
        transitions = transitions.filter((t) => {
          const startTimeMs = new Date(t.startTime).getTime();
          return startTimeMs >= startMs && startTimeMs <= endMs;
        });
      }

      return transitions;
    }, [data.stateTransitions, selectedEsysUuid, startDate, endDate, formatters]);

    // Extract unique values for filter dropdowns
    const uniqueHardwareTypes = useMemo(() => {
      const types = new Set(filteredTransitions.map((t) => t.hardwareType));
      return Array.from(types).sort();
    }, [filteredTransitions]);

    const uniqueSerialNumbers = useMemo(() => {
      const serials = new Set(filteredTransitions.map((t) => t.serialNumber));
      return Array.from(serials).sort();
    }, [filteredTransitions]);

    const uniqueReadingNames = useMemo(() => {
      const names = new Set(filteredTransitions.map((t) => t.readingName));
      return Array.from(names).sort();
    }, [filteredTransitions]);

    const uniqueStates = useMemo(() => {
      const states = new Set(filteredTransitions.map((t) => t.state));
      return Array.from(states).sort();
    }, [filteredTransitions]);

    // Apply filters and sort
    const displayTransitions = useMemo(() => {
      let result = filteredTransitions;

      if (filterHardwareType.length > 0) {
        result = result.filter((t) =>
          filterHardwareType.includes(t.hardwareType)
        );
      }
      if (filterSerialNumber.length > 0) {
        result = result.filter((t) =>
          filterSerialNumber.includes(t.serialNumber)
        );
      }
      if (filterReadingName.length > 0) {
        result = result.filter((t) => filterReadingName.includes(t.readingName));
      }
      if (filterState.length > 0) {
        result = result.filter((t) => filterState.includes(t.state));
      }

      // Sort by Hardware Type, Serial Number, Reading Name, Start Time
      result = [...result].sort((a, b) => {
        const hwCompare = a.hardwareType.localeCompare(b.hardwareType);
        if (hwCompare !== 0) return hwCompare;
        const serialCompare = a.serialNumber.localeCompare(b.serialNumber);
        if (serialCompare !== 0) return serialCompare;
        const nameCompare = a.readingName.localeCompare(b.readingName);
        if (nameCompare !== 0) return nameCompare;
        return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
      });

      return result.slice(0, MAX_TABLE_ROWS);
    }, [filteredTransitions, filterHardwareType, filterSerialNumber, filterReadingName, filterState]);

    const totalFiltered = useMemo(() => {
      let result = filteredTransitions;

      if (filterHardwareType.length > 0) {
        result = result.filter((t) =>
          filterHardwareType.includes(t.hardwareType)
        );
      }
      if (filterSerialNumber.length > 0) {
        result = result.filter((t) =>
          filterSerialNumber.includes(t.serialNumber)
        );
      }
      if (filterReadingName.length > 0) {
        result = result.filter((t) => filterReadingName.includes(t.readingName));
      }
      if (filterState.length > 0) {
        result = result.filter((t) => filterState.includes(t.state));
      }

      return result.length;
    }, [filteredTransitions, filterHardwareType, filterSerialNumber, filterReadingName, filterState]);

    const isTruncated = totalFiltered > MAX_TABLE_ROWS;

    if (filteredTransitions.length === 0) {
      return (
        <Row className="mt-4">
          <Col>
            <div className="alert alert-info" role="alert">
              No state data available for this site in the selected date range
            </div>
          </Col>
        </Row>
      );
    }

    return (
      <>
        <Row className="mt-4">
          <Col>
            <h5>State Transitions</h5>
            <p className="text-muted">
              Showing {displayTransitions.length.toLocaleString()}
              {isTruncated &&
                ` (limited from ${totalFiltered.toLocaleString()})`}{" "}
              of {filteredTransitions.length.toLocaleString()} state transition
              {filteredTransitions.length !== 1 ? "s" : ""} in the selected range
              {data.truncated && (
                <span className="text-warning ms-2">
                  (Data truncated at {data.stateTransitionsLimit.toLocaleString()} transitions)
                </span>
              )}
            </p>
            <Row className="mb-3">
              <Col md={3}>
                <div className="mb-3">
                  <label className="form-label">Hardware Type</label>
                  <Select
                    isMulti
                    options={uniqueHardwareTypes.map((type) => ({
                      value: type,
                      label: type,
                    }))}
                    value={filterHardwareType.map((type) => ({
                      value: type,
                      label: type,
                    }))}
                    onChange={(selected) =>
                      setFilterHardwareType(
                        selected ? selected.map((s) => s.value) : []
                      )
                    }
                    placeholder="All"
                    isClearable
                  />
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <label className="form-label">Serial Number</label>
                  <Select
                    isMulti
                    options={uniqueSerialNumbers.map((serial) => ({
                      value: serial,
                      label: serial,
                    }))}
                    value={filterSerialNumber.map((serial) => ({
                      value: serial,
                      label: serial,
                    }))}
                    onChange={(selected) =>
                      setFilterSerialNumber(
                        selected ? selected.map((s) => s.value) : []
                      )
                    }
                    placeholder="All"
                    isClearable
                  />
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <label className="form-label">Reading Name</label>
                  <Select
                    isMulti
                    options={uniqueReadingNames.map((name) => ({
                      value: name,
                      label: name,
                    }))}
                    value={filterReadingName.map((name) => ({
                      value: name,
                      label: name,
                    }))}
                    onChange={(selected) =>
                      setFilterReadingName(
                        selected ? selected.map((s) => s.value) : []
                      )
                    }
                    placeholder="All"
                    isClearable
                  />
                </div>
              </Col>
              <Col md={3}>
                <div className="mb-3">
                  <label className="form-label">State</label>
                  <Select
                    isMulti
                    options={uniqueStates.map((state) => ({
                      value: state,
                      label: state,
                    }))}
                    value={filterState.map((state) => ({
                      value: state,
                      label: state,
                    }))}
                    onChange={(selected) =>
                      setFilterState(
                        selected ? selected.map((s) => s.value) : []
                      )
                    }
                    placeholder="All"
                    isClearable
                  />
                </div>
              </Col>
            </Row>
            <div>
              <Table striped bordered hover size="sm" responsive>
                <thead>
                  <tr>
                    <th>Hardware Type</th>
                    <th>Serial Number</th>
                    <th>Reading Name</th>
                    <th>State</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {displayTransitions.map((transition, index) => (
                    <tr key={index}>
                      <td>{transition.hardwareType}</td>
                      <td>{transition.serialNumber}</td>
                      <td>{transition.readingName}</td>
                      <td>
                        <code>{transition.state}</code>
                      </td>
                      <td>{formatters.toLocaleString(transition.startTime)}</td>
                      <td>
                        {transition.endTime
                          ? formatters.toLocaleString(transition.endTime)
                          : <span className="text-muted">Ongoing</span>}
                      </td>
                      <td>
                        {formatDuration(transition.startTime, transition.endTime)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      </>
    );
  }
);

StateDataTable.displayName = "StateDataTable";

export default StateDataTable;
